import React, { useState, useEffect } from 'react';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface FeeStructure {
  id: string;
  className: string;
  academicYear: string;
  totalAmount: number;
  installments: Array<{
    id: string;
    name: string;
    amount: number;
    dueDate: string;
    monthDue: string;
  }>;
  feeComponents: any;
}

interface Student {
  id: string;
  name: string;
  class: string;
  section: string;
  rollNumber: string;
}

interface AssignFeesProps {
  domain: string;
}

const AssignFees: React.FC<AssignFeesProps> = ({ domain }) => {
  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([]);
  const [selectedStructure, setSelectedStructure] = useState<FeeStructure | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (domain) {
      loadFeeStructures();
    }
  }, [domain]);

  const loadFeeStructures = async () => {
    setLoading(true);
    try {
      const feeStructuresRef = collection(db, `school-users/${domain}/fee-structures`);
      const snapshot = await getDocs(feeStructuresRef);

      const structures: FeeStructure[] = [];
      snapshot.forEach((doc) => {
        structures.push({ id: doc.id, ...doc.data() } as FeeStructure);
      });

      setFeeStructures(structures.sort((a, b) => a.className.localeCompare(b.className)));
    } catch (error) {
      console.error('Error loading fee structures:', error);
      alert('Failed to load fee structures');
    } finally {
      setLoading(false);
    }
  };

  const loadStudentsByClass = async (className: string) => {
    setLoading(true);
    try {
      const studentsRef = collection(db, `school-users/${domain}/students`);
      const snapshot = await getDocs(studentsRef);

      const studentsList: Student[] = [];
      snapshot.forEach((doc) => {
        const studentData = { id: doc.id, ...doc.data() } as Student;

        // Students store class and section separately (e.g., class: "11", section: "A")
        // Fee structures use combined format (e.g., className: "11-A")
        const studentClass = String(studentData.class || '').trim();
        const studentSection = String(studentData.section || '').trim();
        const combinedClass = studentSection ? `${studentClass}-${studentSection}` : studentClass;

        // Match by combined class-section format or just class
        if (combinedClass === className || studentClass === className) {
          studentsList.push(studentData);
        }
      });

      setStudents(studentsList.sort((a, b) => String(a.rollNumber).localeCompare(String(b.rollNumber))));
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleStructureSelect = async (structureId: string) => {
    const structure = feeStructures.find(s => s.id === structureId);
    if (structure) {
      setSelectedStructure(structure);
      setSelectedStudents(new Set());
      await loadStudentsByClass(structure.className);
    } else {
      setSelectedStructure(null);
      setStudents([]);
      setSelectedStudents(new Set());
    }
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedStudents.size === students.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(students.map(s => s.id)));
    }
  };

  const assignFeesToStudents = async () => {
    if (!selectedStructure) {
      alert('Please select a fee structure');
      return;
    }

    if (selectedStudents.size === 0) {
      alert('Please select at least one student');
      return;
    }

    if (!confirm(`Assign fees to ${selectedStudents.size} student(s)?`)) {
      return;
    }

    setAssignmentLoading(true);
    try {
      const batch = writeBatch(db);
      const assignmentDate = new Date().toISOString();

      for (const studentId of selectedStudents) {
        const student = students.find(s => s.id === studentId);
        if (!student) continue;

        // Create fee assignment document
        const feeAssignmentRef = doc(collection(db, `school-users/${domain}/fee-assignments`));
        batch.set(feeAssignmentRef, {
          studentId: student.id,
          studentName: student.name,
          studentClass: student.class,
          studentSection: student.section,
          rollNumber: student.rollNumber,
          feeStructureId: selectedStructure.id,
          className: selectedStructure.className,
          academicYear: selectedStructure.academicYear,
          totalAmount: selectedStructure.totalAmount,
          feeComponents: selectedStructure.feeComponents,
          installments: selectedStructure.installments.map(inst => ({
            ...inst,
            status: 'pending',
            paidAmount: 0,
            paidDate: null,
            transactionId: null,
          })),
          totalPaid: 0,
          totalPending: selectedStructure.totalAmount,
          status: 'pending',
          assignedAt: assignmentDate,
          updatedAt: assignmentDate,
        });

        // Create individual installment records for tracking
        for (const installment of selectedStructure.installments) {
          const installmentRef = doc(collection(db, `school-users/${domain}/fee-installments`));
          batch.set(installmentRef, {
            studentId: student.id,
            studentName: student.name,
            studentClass: student.class,
            rollNumber: student.rollNumber,
            feeStructureId: selectedStructure.id,
            feeAssignmentId: feeAssignmentRef.id,
            installmentId: installment.id,
            installmentName: installment.name,
            amount: installment.amount,
            dueDate: installment.dueDate,
            monthDue: installment.monthDue,
            status: 'pending',
            paidAmount: 0,
            paidDate: null,
            transactionId: null,
            createdAt: assignmentDate,
          });
        }
      }

      await batch.commit();
      alert(`Successfully assigned fees to ${selectedStudents.size} student(s)!`);
      setSelectedStudents(new Set());
      setShowPreview(false);
    } catch (error) {
      console.error('Error assigning fees:', error);
      alert('Failed to assign fees. Please try again.');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-person-plus-fill me-2 text-primary-custom"></i>
          Assign Fees to Students
        </h5>
        <small className="text-muted">Automatically tag fee structures to students by class</small>
      </div>

      <div className="card-body">
        {/* Step 1: Select Fee Structure */}
        <div className="mb-4">
          <label className="form-label fw-bold">
            <i className="bi bi-1-circle-fill me-2 text-primary"></i>
            Select Fee Structure
          </label>
          <select
            className="form-select form-select-lg"
            value={selectedStructure?.id || ''}
            onChange={(e) => handleStructureSelect(e.target.value)}
            disabled={loading}
          >
            <option value="">-- Select a Fee Structure --</option>
            {feeStructures.map((structure) => (
              <option key={structure.id} value={structure.id}>
                {structure.className} - {structure.academicYear} ({formatCurrency(structure.totalAmount)})
              </option>
            ))}
          </select>
          {feeStructures.length === 0 && !loading && (
            <small className="text-muted d-block mt-2">
              No fee structures available. Please create one in the "Fee Structure" tab first.
            </small>
          )}
        </div>

        {/* Fee Structure Details Preview */}
        {selectedStructure && (
          <div className="card border-primary mb-4">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">Selected Fee Structure - {selectedStructure.className}</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-2"><strong>Academic Year:</strong> {selectedStructure.academicYear}</p>
                  <p className="mb-2"><strong>Total Amount:</strong> <span className="text-success fw-bold">{formatCurrency(selectedStructure.totalAmount)}</span></p>
                </div>
                <div className="col-md-6">
                  <p className="mb-2"><strong>Installments:</strong> {selectedStructure.installments.length}</p>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    <i className={`bi ${showPreview ? 'bi-eye-slash' : 'bi-eye'} me-1`}></i>
                    {showPreview ? 'Hide' : 'Show'} Details
                  </button>
                </div>
              </div>

              {showPreview && (
                <div className="mt-3">
                  <h6 className="text-muted small">Installment Breakdown:</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>Installment</th>
                          <th>Amount</th>
                          <th>Due Date</th>
                          <th>Month</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedStructure.installments.map((inst) => (
                          <tr key={inst.id}>
                            <td>{inst.name}</td>
                            <td>{formatCurrency(inst.amount)}</td>
                            <td>{inst.dueDate}</td>
                            <td>{inst.monthDue}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Select Students */}
        {selectedStructure && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <label className="form-label fw-bold mb-0">
                <i className="bi bi-2-circle-fill me-2 text-primary"></i>
                Select Students from {selectedStructure.className}
              </label>
              {students.length > 0 && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleSelectAll}
                >
                  <i className="bi bi-check-all me-1"></i>
                  {selectedStudents.size === students.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading students...</span>
                </div>
              </div>
            ) : students.length === 0 ? (
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                No students found in {selectedStructure.className}
              </div>
            ) : (
              <>
                <div className="table-responsive mb-3">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedStudents.size === students.length}
                            onChange={toggleSelectAll}
                          />
                        </th>
                        <th>Roll No</th>
                        <th>Student Name</th>
                        <th>Class</th>
                        <th>Section</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => (
                        <tr
                          key={student.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleStudentSelection(student.id)}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedStudents.has(student.id)}
                              onChange={() => toggleStudentSelection(student.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td>{student.rollNumber}</td>
                          <td>{student.name}</td>
                          <td>{student.class}</td>
                          <td>{student.section}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Summary and Action */}
                <div className="card border-success">
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-md-8">
                        <h6 className="mb-2">Assignment Summary</h6>
                        <ul className="list-unstyled mb-0">
                          <li><i className="bi bi-check-circle text-success me-2"></i>
                            <strong>{selectedStudents.size}</strong> student(s) selected
                          </li>
                          <li><i className="bi bi-cash-stack text-success me-2"></i>
                            Fee Amount: <strong>{formatCurrency(selectedStructure.totalAmount)}</strong> per student
                          </li>
                          <li><i className="bi bi-calculator text-success me-2"></i>
                            Total Amount: <strong>{formatCurrency(selectedStructure.totalAmount * selectedStudents.size)}</strong>
                          </li>
                        </ul>
                      </div>
                      <div className="col-md-4 text-end">
                        <button
                          className="btn btn-success btn-lg"
                          onClick={assignFeesToStudents}
                          disabled={selectedStudents.size === 0 || assignmentLoading}
                        >
                          {assignmentLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Assigning...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-check-lg me-2"></i>
                              Assign Fees
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Initial State */}
        {!selectedStructure && !loading && (
          <div className="text-center text-muted py-5">
            <i className="bi bi-arrow-up-circle fs-1 d-block mb-3"></i>
            <p>Select a fee structure above to begin assigning fees to students</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignFees;
